import { Node, mergeAttributes } from '@tiptap/core';
import { VueNodeViewRenderer } from '@tiptap/vue-3';
import NumberedTableNodeView from './NumberedTableNodeView.vue';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        numberedTable: {
            /** Insert a new numbered table wrapper at the current position. */
            insertNumberedTable: (options?: {
                rows?: number;
                cols?: number;
                caption?: string;
            }) => ReturnType;
        };
    }
}

/**
 * A block wrapper that pairs a native TipTap table with an editable caption
 * and an auto-computed sequential number ("Tableau N : <caption>").
 *
 * The node itself holds caption text as an attribute; the actual table lives
 * inside it as a regular ProseMirror table node.
 */
export const NumberedTableExtension = Node.create({
    name: 'numberedTable',

    group: 'block',

    // Contains exactly one table node
    content: 'table',

    // Caption is stored as an attribute so it survives serialisation
    addAttributes() {
        return {
            tableId: {
                default: null,
                parseHTML: (el) => el.getAttribute('data-table-id'),
                renderHTML: (attrs) => ({ 'data-table-id': attrs.tableId }),
            },
            caption: {
                default: '',
                parseHTML: (el) =>
                    el.querySelector('[data-caption]')?.textContent ?? '',
                renderHTML: () => ({}), // caption is handled by the NodeView header
            },
        };
    },

    parseHTML() {
        return [{ tag: 'div[data-numbered-table]' }];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, { 'data-numbered-table': '' }),
            0,
        ];
    },

    addNodeView() {
        return VueNodeViewRenderer(NumberedTableNodeView);
    },

    addCommands() {
        return {
            insertNumberedTable:
                ({ rows = 3, cols = 3, caption = '' } = {}) =>
                ({ commands, state }) => {
                    const { schema } = state;

                    // Build an uuid-like tableId (crypto.randomUUID when available)
                    const tableId =
                        typeof crypto !== 'undefined' && crypto.randomUUID
                            ? crypto.randomUUID()
                            : Math.random().toString(36).slice(2);

                    // Build the inner native table
                    const headerCells = Array.from({ length: cols }, () =>
                        schema.nodes.tableHeader.create(
                            {},
                            schema.nodes.paragraph.create(),
                        ),
                    );
                    const headerRow = schema.nodes.tableRow.create(
                        {},
                        headerCells,
                    );

                    const bodyRows = Array.from({ length: rows - 1 }, () => {
                        const cells = Array.from({ length: cols }, () =>
                            schema.nodes.tableCell.create(
                                {},
                                schema.nodes.paragraph.create(),
                            ),
                        );

                        return schema.nodes.tableRow.create({}, cells);
                    });

                    const table = schema.nodes.table.create({}, [
                        headerRow,
                        ...bodyRows,
                    ]);

                    const wrapper = schema.nodes.numberedTable.create(
                        { tableId, caption },
                        table,
                    );

                    return commands.insertContent(wrapper.toJSON());
                },
        };
    },
});
